import { ImageElement, Page, TextElement } from '../types/storybook';
import { NormalizedRegion, PrintSettings } from '../types/layout';

export type PreflightSeverity = 'error' | 'warning';

export type PreflightIssueCode =
  | 'CANVAS_GEOMETRY_INVALID'
  | 'ELEMENT_GEOMETRY_NON_FINITE'
  | 'ELEMENT_GEOMETRY_NON_POSITIVE'
  | 'ELEMENT_OUTSIDE_TRIM'
  | 'ELEMENT_CLIPPED'
  | 'TEXT_OUTSIDE_SAFE_AREA'
  | 'TEXT_TOO_SMALL'
  | 'TEXT_LOW_CONTRAST'
  | 'TEXT_CONTRAST_UNVERIFIED'
  | 'TEXT_COLLISION'
  | 'TEXT_SUBJECT_OVERLAP'
  | 'SEMANTIC_REGIONS_MISSING'
  | 'SEMANTIC_REGION_INVALID'
  | 'FULL_BLEED_COVERAGE_MISSING'
  | 'EDGE_BLEED_FIT_MISSING'
  | 'IMAGE_URL_MISSING'
  | 'IMAGE_DPI_LOW'
  | 'IMAGE_DPI_UNKNOWN'
  | 'TEXT_FONT_MISSING'
  | 'TEXT_OVERFLOW'
  | 'TEXT_MEASUREMENT_FAILED';

export type PreflightAutoFixKind =
  | 'move-element-inside-trim'
  | 'move-text-inside-safe-area'
  | 'reflow-readable-text'
  | 'use-production-font'
  | 'use-cover-fit'
  | 'increase-text-contrast'
  | 'add-readable-text-panel';

export interface PreflightElementPatch {
  elementType: 'text' | 'image';
  elementId: string;
  updates: Partial<TextElement> | Partial<ImageElement>;
}

/**
 * A fix descriptor is deliberately data-only. The editor can show it directly,
 * apply `patch` through its normal save path, or route `requiresReflow` fixes to
 * the production-font-aware pagination workflow.
 */
export interface PreflightAutoFixDescriptor {
  kind: PreflightAutoFixKind;
  label: string;
  safe: true;
  patch?: PreflightElementPatch;
  requiresReflow?: boolean;
}

export interface PreflightIssueMetrics {
  contrastRatio?: number;
  recommendedContrastRatio?: number;
  criticalContrastRatio?: number;
  recommendedTextColor?: string;
  effectiveDpi?: number;
  targetDpi?: number;
  criticalDpi?: number;
  naturalWidth?: number;
  naturalHeight?: number;
}

export interface PreflightIssue {
  code: PreflightIssueCode;
  severity: PreflightSeverity;
  pageId: string;
  elementId: string | null;
  message: string;
  suggestion: string;
  relatedElementId?: string;
  regionId?: string;
  autoFix?: PreflightAutoFixDescriptor;
  metrics?: PreflightIssueMetrics;
}

export interface PreflightRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface TextOverflowMeasurement {
  overflow: boolean;
  clientWidth?: number;
  clientHeight?: number;
  scrollWidth?: number;
  scrollHeight?: number;
}

export interface TextOverflowMeasureInput {
  page: Page;
  textElement: TextElement;
  bounds: PreflightRect;
}

export type TextOverflowMeasurer = (
  input: TextOverflowMeasureInput
) => boolean | TextOverflowMeasurement | Promise<boolean | TextOverflowMeasurement>;

export interface ImageMetadataProbeInput {
  page: Page;
  imageElement: ImageElement;
  bounds: PreflightRect;
}

export interface ImageMetadataMeasurement {
  width: number;
  height: number;
}

export type ImageMetadataProbe = (
  input: ImageMetadataProbeInput
) => ImageMetadataMeasurement | Promise<ImageMetadataMeasurement>;

export interface TextContrastMeasureInput {
  page: Page;
  textElement: TextElement;
  bounds: PreflightRect;
}

export interface TextContrastMeasurement {
  ratio: number;
  backgroundColor?: string;
}

export type TextContrastMeasurer = (
  input: TextContrastMeasureInput
) => number | TextContrastMeasurement | Promise<number | TextContrastMeasurement>;

export interface PagePreflightOptions {
  canvasSize: { width: number; height: number };
  printSettings?: PrintSettings;
  pixelsPerInch?: number;
  minimumFontSize?: number;
  measureTextOverflow?: TextOverflowMeasurer;
  probeImageMetadata?: ImageMetadataProbe;
  measureTextContrast?: TextContrastMeasurer;
  targetImageDpi?: number;
  criticalImageDpi?: number;
  recommendedContrastRatio?: number;
  criticalContrastRatio?: number;
}

export interface PreflightSummary {
  total: number;
  errorCount: number;
  warningCount: number;
  hasErrors: boolean;
  hasWarnings: boolean;
  isClean: boolean;
  canExport: boolean;
  byCode: Partial<Record<PreflightIssueCode, number>>;
}

type GeometryElement = Pick<ImageElement | TextElement, 'x' | 'y' | 'width' | 'height' | 'rotation'>;

const DEFAULT_PIXELS_PER_INCH = 100;
const DEFAULT_SAFE_MARGIN_INCHES = 0.25;
export const MINIMUM_PREFLIGHT_FONT_SIZE = 18;
export const TARGET_PRINT_DPI = 300;
export const CRITICAL_PRINT_DPI = 150;
export const RECOMMENDED_TEXT_CONTRAST_RATIO = 4.5;
export const CRITICAL_TEXT_CONTRAST_RATIO = 3;
const BOUNDS_EPSILON = 0.01;
const SPEECH_BUBBLE_TAIL_HEIGHT = 30;

const isFiniteNumber = (value: unknown): value is number => (
  typeof value === 'number' && Number.isFinite(value)
);

const hasFiniteGeometry = (element: GeometryElement) => (
  isFiniteNumber(element.x)
  && isFiniteNumber(element.y)
  && isFiniteNumber(element.width)
  && isFiniteNumber(element.height)
  && (element.rotation === undefined || isFiniteNumber(element.rotation))
);

const hasPositiveGeometry = (element: GeometryElement) => element.width > 0 && element.height > 0;

const unionBounds = (first: PreflightRect, second: PreflightRect): PreflightRect => {
  const left = Math.min(first.left, second.left);
  const top = Math.min(first.top, second.top);
  const right = Math.max(first.right, second.right);
  const bottom = Math.max(first.bottom, second.bottom);
  return { left, top, right, bottom, width: right - left, height: bottom - top };
};

const expandBounds = (
  bounds: PreflightRect,
  expansion: { left: number; top: number; right: number; bottom: number }
): PreflightRect => {
  const left = bounds.left - expansion.left;
  const top = bounds.top - expansion.top;
  const right = bounds.right + expansion.right;
  const bottom = bounds.bottom + expansion.bottom;
  return { left, top, right, bottom, width: right - left, height: bottom - top };
};

/** Returns the axis-aligned visual bounds of a rectangle rotated around its center. */
export const getRotationAwareBounds = (element: GeometryElement): PreflightRect => {
  const rotation = ((element.rotation || 0) * Math.PI) / 180;
  const cosine = Math.abs(Math.cos(rotation));
  const sine = Math.abs(Math.sin(rotation));
  const width = element.width * cosine + element.height * sine;
  const height = element.width * sine + element.height * cosine;
  const centerX = element.x + element.width / 2;
  const centerY = element.y + element.height / 2;
  const left = centerX - width / 2;
  const top = centerY - height / 2;

  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height
  };
};

const isSpeechBubble = (text: TextElement) => Boolean(text.styleId?.includes('bubble'));

const getTextVisualBounds = (text: TextElement): PreflightRect => {
  const textBounds = getRotationAwareBounds(text);
  if (!isSpeechBubble(text)) return textBounds;

  // Canvas renders the bubble tail below the rotated text block. Include it in
  // trim, safe-area, and collision checks even though it is not in text.height.
  const bubbleBounds: PreflightRect = {
    left: text.x,
    top: text.y,
    right: text.x + text.width,
    bottom: text.y + text.height + SPEECH_BUBBLE_TAIL_HEIGHT,
    width: text.width,
    height: text.height + SPEECH_BUBBLE_TAIL_HEIGHT
  };
  return unionBounds(textBounds, bubbleBounds);
};

/**
 * Includes paint that the page's overflow:hidden trim can clip even when the
 * element's nominal rectangle is valid: borders, text shadows, and title depth.
 */
const getTextPaintBounds = (text: TextElement): PreflightRect => {
  const bounds = getTextVisualBounds(text);
  const border = Math.max(0, text.borderWidth || 0) / 2;
  const titleDepth = text.styleId === '3d-title' ? 8 : 0;
  const shadowBlur = text.textShadow ? Math.max(0, text.shadowBlur ?? 4) : 0;
  const shadowOffsetX = text.textShadow ? (text.shadowOffsetX ?? 2) : 0;
  const shadowOffsetY = text.textShadow ? (text.shadowOffsetY ?? 2) : 0;
  return expandBounds(bounds, {
    left: border + titleDepth + shadowBlur + Math.max(0, -shadowOffsetX),
    top: border + titleDepth + shadowBlur + Math.max(0, -shadowOffsetY),
    right: border + titleDepth + shadowBlur + Math.max(0, shadowOffsetX),
    bottom: border + titleDepth + shadowBlur + Math.max(0, shadowOffsetY)
  });
};

const getImagePaintBounds = (image: ImageElement): PreflightRect => {
  const border = Math.max(0, image.borderWidth || 0) / 2;
  return expandBounds(getRotationAwareBounds(image), {
    left: border,
    top: border,
    right: border,
    bottom: border
  });
};

const containsBounds = (container: PreflightRect, candidate: PreflightRect) => (
  candidate.left >= container.left - BOUNDS_EPSILON
  && candidate.top >= container.top - BOUNDS_EPSILON
  && candidate.right <= container.right + BOUNDS_EPSILON
  && candidate.bottom <= container.bottom + BOUNDS_EPSILON
);

const translatedIntoBounds = (
  element: GeometryElement,
  visualBounds: PreflightRect,
  targetBounds: PreflightRect
): Pick<GeometryElement, 'x' | 'y'> | null => {
  if (visualBounds.width > targetBounds.width + BOUNDS_EPSILON
    || visualBounds.height > targetBounds.height + BOUNDS_EPSILON) {
    return null;
  }

  let deltaX = 0;
  let deltaY = 0;
  if (visualBounds.left < targetBounds.left) deltaX = targetBounds.left - visualBounds.left;
  else if (visualBounds.right > targetBounds.right) deltaX = targetBounds.right - visualBounds.right;
  if (visualBounds.top < targetBounds.top) deltaY = targetBounds.top - visualBounds.top;
  else if (visualBounds.bottom > targetBounds.bottom) deltaY = targetBounds.bottom - visualBounds.bottom;

  return { x: element.x + deltaX, y: element.y + deltaY };
};

const intersects = (first: PreflightRect, second: PreflightRect) => !(
  first.right <= second.left + BOUNDS_EPSILON
  || second.right <= first.left + BOUNDS_EPSILON
  || first.bottom <= second.top + BOUNDS_EPSILON
  || second.bottom <= first.top + BOUNDS_EPSILON
);

interface RgbaColor { r: number; g: number; b: number; a: number }

const clampColorChannel = (value: number) => Math.max(0, Math.min(255, value));
const clampOpacity = (value: number) => Math.max(0, Math.min(1, value));

const parseCssColor = (input: unknown): RgbaColor | null => {
  const value = String(input || '').trim().toLowerCase();
  if (!value || value === 'transparent') return value === 'transparent'
    ? { r: 0, g: 0, b: 0, a: 0 }
    : null;
  if (value === 'black') return { r: 0, g: 0, b: 0, a: 1 };
  if (value === 'white') return { r: 255, g: 255, b: 255, a: 1 };

  const hex = value.match(/^#([0-9a-f]{3,8})$/i)?.[1];
  if (hex) {
    if (hex.length === 3 || hex.length === 4) {
      return {
        r: parseInt(`${hex[0]}${hex[0]}`, 16),
        g: parseInt(`${hex[1]}${hex[1]}`, 16),
        b: parseInt(`${hex[2]}${hex[2]}`, 16),
        a: hex.length === 4 ? parseInt(`${hex[3]}${hex[3]}`, 16) / 255 : 1
      };
    }
    if (hex.length === 6 || hex.length === 8) {
      return {
        r: parseInt(hex.slice(0, 2), 16),
        g: parseInt(hex.slice(2, 4), 16),
        b: parseInt(hex.slice(4, 6), 16),
        a: hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1
      };
    }
  }

  const rgb = value.match(/^rgba?\(\s*([+-]?[\d.]+)\s*,\s*([+-]?[\d.]+)\s*,\s*([+-]?[\d.]+)(?:\s*,\s*([+-]?[\d.]+)%?)?\s*\)$/);
  if (!rgb) return null;
  const alphaValue = rgb[4] === undefined ? 1 : Number(rgb[4]);
  return {
    r: clampColorChannel(Number(rgb[1])),
    g: clampColorChannel(Number(rgb[2])),
    b: clampColorChannel(Number(rgb[3])),
    a: clampOpacity(alphaValue > 1 ? alphaValue / 100 : alphaValue)
  };
};

const compositeColor = (foreground: RgbaColor, background: RgbaColor): RgbaColor => {
  const alpha = foreground.a + background.a * (1 - foreground.a);
  if (alpha <= 0) return { r: 0, g: 0, b: 0, a: 0 };
  return {
    r: (foreground.r * foreground.a + background.r * background.a * (1 - foreground.a)) / alpha,
    g: (foreground.g * foreground.a + background.g * background.a * (1 - foreground.a)) / alpha,
    b: (foreground.b * foreground.a + background.b * background.a * (1 - foreground.a)) / alpha,
    a: alpha
  };
};

const relativeLuminance = (color: RgbaColor) => {
  const linear = [color.r, color.g, color.b].map((channel) => {
    const normalized = channel / 255;
    return normalized <= 0.04045
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
};

export const calculateContrastRatio = (foreground: string, background: string): number | null => {
  const foregroundColor = parseCssColor(foreground);
  const backgroundColor = parseCssColor(background);
  if (!foregroundColor || !backgroundColor || backgroundColor.a < 1) return null;
  const paintedForeground = compositeColor(foregroundColor, backgroundColor);
  const foregroundLuminance = relativeLuminance(paintedForeground);
  const backgroundLuminance = relativeLuminance(backgroundColor);
  return (Math.max(foregroundLuminance, backgroundLuminance) + 0.05)
    / (Math.min(foregroundLuminance, backgroundLuminance) + 0.05);
};

const cssColor = (color: RgbaColor) => (
  `rgba(${Math.round(color.r)}, ${Math.round(color.g)}, ${Math.round(color.b)}, ${color.a})`
);

const isBoldText = (fontWeight: unknown) => {
  if (String(fontWeight || '').toLowerCase() === 'bold') return true;
  const numericWeight = Number(fontWeight);
  return Number.isFinite(numericWeight) && numericWeight >= 700;
};

const recommendedContrastForText = (
  text: TextElement,
  regularTextTarget: number,
  criticalTarget: number
) => (
  text.fontSize >= 24 || (text.fontSize >= 18 && isBoldText(text.fontWeight))
    ? criticalTarget
    : regularTextTarget
);

const hasOpaqueTextPanel = (text: TextElement) => {
  const hasDefaultPanel = isSpeechBubble(text) || text.styleId === 'tear-box';
  const panelColor = parseCssColor(text.backgroundColor || (hasDefaultPanel ? '#ffffff' : 'transparent'));
  const rawOpacity = text.backgroundOpacity ?? 1;
  const configuredOpacity = clampOpacity(rawOpacity > 1 ? rawOpacity / 100 : rawOpacity);
  return Boolean(panelColor && panelColor.a * configuredOpacity >= 1 - BOUNDS_EPSILON);
};

const resolveStaticTextBackdrop = (
  page: Page,
  text: TextElement,
  overlapsImage: boolean
): RgbaColor | null => {
  const pageColor = parseCssColor(page.backgroundColor || '#ffffff');
  if (!pageColor) return null;
  const opaquePageColor = pageColor.a < 1
    ? compositeColor(pageColor, { r: 255, g: 255, b: 255, a: 1 })
    : pageColor;
  const hasDefaultPanel = isSpeechBubble(text) || text.styleId === 'tear-box';
  const panelColor = parseCssColor(text.backgroundColor || (hasDefaultPanel ? '#ffffff' : 'transparent'));
  if (!panelColor) return null;
  const rawOpacity = text.backgroundOpacity ?? 1;
  const configuredOpacity = clampOpacity(rawOpacity > 1 ? rawOpacity / 100 : rawOpacity);
  const configuredPanel = { ...panelColor, a: panelColor.a * configuredOpacity };

  if (configuredPanel.a >= 1 - BOUNDS_EPSILON) return { ...configuredPanel, a: 1 };
  if (overlapsImage) return null;
  return compositeColor(configuredPanel, opaquePageColor);
};

const normalizedContrastMeasurement = (
  measurement: number | TextContrastMeasurement
): TextContrastMeasurement | null => {
  const result = typeof measurement === 'number' ? { ratio: measurement } : measurement;
  return isFiniteNumber(result?.ratio) && result.ratio >= 1 ? result : null;
};

const isVectorImage = (url: string) => (
  /^data:image\/svg\+xml[;,]/i.test(url) || /\.svg(?:$|[?#])/i.test(url)
);

const calculateEffectiveDpi = (
  image: ImageElement,
  naturalWidth: number,
  naturalHeight: number,
  pixelsPerInch: number,
  canvasSize: PagePreflightOptions['canvasSize'],
  bleedInches: number
) => {
  let displayWidth = image.width;
  let displayHeight = image.height;
  const unrotated = Math.abs(image.rotation || 0) <= BOUNDS_EPSILON;
  if (bleedInches > 0 && unrotated && (image.bleed === 'full' || image.fit === 'cover')) {
    const bleedPixels = bleedInches * pixelsPerInch;
    if (image.bleed === 'full' || image.x <= BOUNDS_EPSILON) displayWidth += bleedPixels;
    if (image.bleed === 'full' || image.x + image.width >= canvasSize.width - BOUNDS_EPSILON) displayWidth += bleedPixels;
    if (image.bleed === 'full' || image.y <= BOUNDS_EPSILON) displayHeight += bleedPixels;
    if (image.bleed === 'full' || image.y + image.height >= canvasSize.height - BOUNDS_EPSILON) displayHeight += bleedPixels;
  }

  const widthDpi = naturalWidth / (displayWidth / pixelsPerInch);
  const heightDpi = naturalHeight / (displayHeight / pixelsPerInch);
  return (image.fit || 'contain') === 'cover'
    ? Math.min(widthDpi, heightDpi)
    : Math.max(widthDpi, heightDpi);
};

const issue = (
  page: Page,
  code: PreflightIssueCode,
  severity: PreflightSeverity,
  elementId: string | null,
  message: string,
  suggestion: string,
  details?: Pick<PreflightIssue, 'relatedElementId' | 'regionId' | 'autoFix' | 'metrics'>
): PreflightIssue => ({
  code,
  severity,
  pageId: page.id,
  elementId,
  message,
  suggestion,
  ...details
});

const patchFix = (
  kind: PreflightAutoFixKind,
  label: string,
  elementType: PreflightElementPatch['elementType'],
  elementId: string,
  updates: PreflightElementPatch['updates']
): PreflightAutoFixDescriptor => ({
  kind,
  label,
  safe: true,
  patch: { elementType, elementId, updates }
});

const reflowFix = (
  kind: Extract<PreflightAutoFixKind, 'reflow-readable-text' | 'use-production-font'>,
  label: string
): PreflightAutoFixDescriptor => ({
  kind,
  label,
  safe: true,
  requiresReflow: true
});

export const applyPreflightAutoFix = (
  page: Page,
  fix: PreflightAutoFixDescriptor
): Page => {
  if (!fix.patch || fix.requiresReflow) return page;
  const { elementType, elementId, updates } = fix.patch;
  if (elementType === 'text') {
    return {
      ...page,
      textElements: (page.textElements || []).map((text) => (
        text.id === elementId ? { ...text, ...(updates as Partial<TextElement>) } : text
      ))
    };
  }
  return {
    ...page,
    imageElements: (page.imageElements || []).map((image) => (
      image.id === elementId ? { ...image, ...(updates as Partial<ImageElement>) } : image
    ))
  };
};

const normalizedRegionIsValid = (region: NormalizedRegion) => (
  isFiniteNumber(region.x)
  && isFiniteNumber(region.y)
  && isFiniteNumber(region.width)
  && isFiniteNumber(region.height)
  && region.width > 0
  && region.height > 0
  && region.x >= 0
  && region.y >= 0
  && region.x + region.width <= 1 + BOUNDS_EPSILON
  && region.y + region.height <= 1 + BOUNDS_EPSILON
);

const regionToCanvasBounds = (
  region: NormalizedRegion,
  canvasSize: PagePreflightOptions['canvasSize']
): PreflightRect => {
  const left = region.x * canvasSize.width;
  const top = region.y * canvasSize.height;
  const width = region.width * canvasSize.width;
  const height = region.height * canvasSize.height;
  return { left, top, right: left + width, bottom: top + height, width, height };
};

const pointIsInsideRotatedRect = (
  point: { x: number; y: number },
  element: GeometryElement
) => {
  const centerX = element.x + element.width / 2;
  const centerY = element.y + element.height / 2;
  const radians = (-1 * (element.rotation || 0) * Math.PI) / 180;
  const deltaX = point.x - centerX;
  const deltaY = point.y - centerY;
  const localX = deltaX * Math.cos(radians) - deltaY * Math.sin(radians) + centerX;
  const localY = deltaX * Math.sin(radians) + deltaY * Math.cos(radians) + centerY;

  return localX >= element.x - BOUNDS_EPSILON
    && localX <= element.x + element.width + BOUNDS_EPSILON
    && localY >= element.y - BOUNDS_EPSILON
    && localY <= element.y + element.height + BOUNDS_EPSILON;
};

const coversTrim = (
  image: ImageElement,
  canvasSize: PagePreflightOptions['canvasSize']
) => [
  { x: 0, y: 0 },
  { x: canvasSize.width, y: 0 },
  { x: 0, y: canvasSize.height },
  { x: canvasSize.width, y: canvasSize.height }
].every((corner) => pointIsInsideRotatedRect(corner, image));

const overflowResultIsTrue = (result: boolean | TextOverflowMeasurement) => (
  typeof result === 'boolean' ? result : result.overflow
);

export const preflightPage = async (
  page: Page,
  options: PagePreflightOptions
): Promise<PreflightIssue[]> => {
  const issues: PreflightIssue[] = [];
  const { canvasSize } = options;
  const canvasIsValid = isFiniteNumber(canvasSize.width)
    && isFiniteNumber(canvasSize.height)
    && canvasSize.width > 0
    && canvasSize.height > 0;

  if (!canvasIsValid) {
    return [issue(
      page,
      'CANVAS_GEOMETRY_INVALID',
      'error',
      null,
      'The page canvas does not have finite, positive dimensions.',
      'Choose a supported book size before laying out or exporting this page.'
    )];
  }

  const trimBounds: PreflightRect = {
    left: 0,
    top: 0,
    right: canvasSize.width,
    bottom: canvasSize.height,
    width: canvasSize.width,
    height: canvasSize.height
  };
  const pixelsPerInch = isFiniteNumber(options.pixelsPerInch) && options.pixelsPerInch > 0
    ? options.pixelsPerInch
    : DEFAULT_PIXELS_PER_INCH;
  const safeMarginInches = options.printSettings?.safeMarginInches ?? DEFAULT_SAFE_MARGIN_INCHES;
  const safeMargin = Math.max(0, safeMarginInches * pixelsPerInch);
  const safeBounds: PreflightRect = {
    left: safeMargin,
    top: safeMargin,
    right: canvasSize.width - safeMargin,
    bottom: canvasSize.height - safeMargin,
    width: canvasSize.width - safeMargin * 2,
    height: canvasSize.height - safeMargin * 2
  };
  const minimumFontSize = options.minimumFontSize ?? MINIMUM_PREFLIGHT_FONT_SIZE;
  const targetImageDpi = options.targetImageDpi ?? TARGET_PRINT_DPI;
  const criticalImageDpi = options.criticalImageDpi ?? CRITICAL_PRINT_DPI;
  const recommendedContrastRatio = options.recommendedContrastRatio
    ?? RECOMMENDED_TEXT_CONTRAST_RATIO;
  const criticalContrastRatio = options.criticalContrastRatio
    ?? CRITICAL_TEXT_CONTRAST_RATIO;

  const imageBounds = new Map<string, PreflightRect>();
  const textBounds = new Map<string, PreflightRect>();
  const validImageIds = new Set<string>();
  const validTextIds = new Set<string>();

  for (const image of page.imageElements || []) {
    const imageUrl = String(image.url || '').trim();
    if (!imageUrl) {
      issues.push(issue(
        page,
        'IMAGE_URL_MISSING',
        'error',
        image.id,
        'This image element has no image URL.',
        'Replace or remove the missing image before export.'
      ));
    }

    if (!hasFiniteGeometry(image)) {
      issues.push(issue(
        page,
        'ELEMENT_GEOMETRY_NON_FINITE',
        'error',
        image.id,
        'This image has a non-finite position, size, or rotation.',
        'Reset the image position and dimensions to finite numeric values.'
      ));
      continue;
    }
    if (!hasPositiveGeometry(image)) {
      issues.push(issue(
        page,
        'ELEMENT_GEOMETRY_NON_POSITIVE',
        'error',
        image.id,
        'This image must have a positive width and height.',
        'Resize or remove the zero-sized image.'
      ));
      continue;
    }

    validImageIds.add(image.id);
    const bounds = getRotationAwareBounds(image);
    const paintBounds = getImagePaintBounds(image);
    imageBounds.set(image.id, paintBounds);
    if (image.bleed !== 'full' && !containsBounds(trimBounds, bounds)) {
      const translated = translatedIntoBounds(image, paintBounds, trimBounds);
      issues.push(issue(
        page,
        'ELEMENT_OUTSIDE_TRIM',
        'error',
        image.id,
        'This image extends outside the page trim boundary.',
        'Move or resize the image so it stays inside the page, or explicitly mark it as full bleed.',
        translated ? {
          autoFix: patchFix(
            'move-element-inside-trim',
            'Move image inside trim',
            'image',
            image.id,
            translated
          )
        } : undefined
      ));
    } else if (image.bleed !== 'full' && !containsBounds(trimBounds, paintBounds)) {
      const translated = translatedIntoBounds(image, paintBounds, trimBounds);
      issues.push(issue(
        page,
        'ELEMENT_CLIPPED',
        'error',
        image.id,
        'This image border extends past trim and will be clipped in the exported page.',
        'Move the complete painted image inside trim or remove the edge border.',
        translated ? {
          autoFix: patchFix(
            'move-element-inside-trim',
            'Move painted image inside trim',
            'image',
            image.id,
            translated
          )
        } : undefined
      ));
    }

    const hasCachedMetadata = isFiniteNumber(image.naturalWidth)
      && isFiniteNumber(image.naturalHeight)
      && image.naturalWidth > 0
      && image.naturalHeight > 0;
    const hasInvalidCachedMetadata = image.naturalWidth !== undefined || image.naturalHeight !== undefined;
    if (imageUrl && !isVectorImage(imageUrl)
      && (hasCachedMetadata || hasInvalidCachedMetadata || options.probeImageMetadata)) {
      try {
        const metadata = hasCachedMetadata
          ? { width: image.naturalWidth!, height: image.naturalHeight! }
          : options.probeImageMetadata
            ? await options.probeImageMetadata({ page, imageElement: image, bounds })
            : null;
        if (!metadata || !isFiniteNumber(metadata.width) || !isFiniteNumber(metadata.height)
          || metadata.width <= 0 || metadata.height <= 0) {
          issues.push(issue(
            page,
            'IMAGE_DPI_UNKNOWN',
            'warning',
            image.id,
            'The image pixel dimensions could not be verified for print.',
            'Reload or replace the image, then run Layout Check again before ordering a printed copy.'
          ));
        } else {
          const effectiveDpi = calculateEffectiveDpi(
            image,
            metadata.width,
            metadata.height,
            pixelsPerInch,
            canvasSize,
            options.printSettings?.bleedInches || 0
          );
          if (effectiveDpi < targetImageDpi - BOUNDS_EPSILON) {
            const severity: PreflightSeverity = effectiveDpi < criticalImageDpi
              ? 'error'
              : 'warning';
            issues.push(issue(
              page,
              'IMAGE_DPI_LOW',
              severity,
              image.id,
              `This image has about ${Math.round(effectiveDpi)} effective DPI at its printed size${severity === 'error' ? `, below the ${criticalImageDpi} DPI blocking minimum` : `, below the ${targetImageDpi} DPI print target`}.`,
              'Use a higher-resolution image, upscale it, or reduce its printed size.',
              {
                metrics: {
                  effectiveDpi,
                  targetDpi: targetImageDpi,
                  criticalDpi: criticalImageDpi,
                  naturalWidth: metadata.width,
                  naturalHeight: metadata.height
                }
              }
            ));
          }
        }
      } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);
        issues.push(issue(
          page,
          'IMAGE_DPI_UNKNOWN',
          'warning',
          image.id,
          `The image pixel dimensions could not be verified${reason ? `: ${reason}` : '.'}`,
          'Reload or replace the image, then run Layout Check again before ordering a printed copy.'
        ));
      }
    }
  }

  for (const text of page.textElements || []) {
    if (!String(text.fontFamily || '').trim()) {
      issues.push(issue(
        page,
        'TEXT_FONT_MISSING',
        'error',
        text.id,
        'This text element has no font family.',
        'Choose a supported production font before export.',
        {
          autoFix: reflowFix(
            'use-production-font',
            'Use the production font and reflow'
          )
        }
      ));
    }

    if (!hasFiniteGeometry(text) || !isFiniteNumber(text.fontSize)) {
      issues.push(issue(
        page,
        'ELEMENT_GEOMETRY_NON_FINITE',
        'error',
        text.id,
        'This text has a non-finite position, size, rotation, or font size.',
        'Reset the text position, dimensions, rotation, and font size to finite numeric values.'
      ));
      continue;
    }
    if (!hasPositiveGeometry(text)) {
      issues.push(issue(
        page,
        'ELEMENT_GEOMETRY_NON_POSITIVE',
        'error',
        text.id,
        'This text box must have a positive width and height.',
        'Resize or remove the zero-sized text box.'
      ));
      continue;
    }

    validTextIds.add(text.id);
    const layoutBounds = getTextVisualBounds(text);
    const bounds = getTextPaintBounds(text);
    textBounds.set(text.id, bounds);
    if (!containsBounds(trimBounds, layoutBounds)) {
      const translated = translatedIntoBounds(text, bounds, trimBounds);
      issues.push(issue(
        page,
        'ELEMENT_OUTSIDE_TRIM',
        'error',
        text.id,
        'This text extends outside the page trim boundary.',
        'Move, resize, or unrotate the text so its complete visual bounds stay on the page.',
        translated ? {
          autoFix: patchFix(
            'move-element-inside-trim',
            'Move text inside trim',
            'text',
            text.id,
            translated
          )
        } : undefined
      ));
    } else if (!containsBounds(trimBounds, bounds)) {
      const translated = translatedIntoBounds(text, bounds, trimBounds);
      issues.push(issue(
        page,
        'ELEMENT_CLIPPED',
        'error',
        text.id,
        'A border, shadow, title depth, or speech-bubble tail extends past trim and will be clipped.',
        'Move the complete painted text inside trim or reduce the effect.',
        translated ? {
          autoFix: patchFix(
            'move-element-inside-trim',
            'Move painted text inside trim',
            'text',
            text.id,
            translated
          )
        } : undefined
      ));
    }
    if (!containsBounds(safeBounds, bounds)) {
      const translated = translatedIntoBounds(text, bounds, safeBounds);
      issues.push(issue(
        page,
        'TEXT_OUTSIDE_SAFE_AREA',
        'error',
        text.id,
        'This text extends outside the configured print-safe area.',
        'Move or resize the text inside the safe-area guide.',
        translated ? {
          autoFix: patchFix(
            'move-text-inside-safe-area',
            'Move text inside the safe area',
            'text',
            text.id,
            translated
          )
        } : {
          autoFix: reflowFix(
            'reflow-readable-text',
            'Reflow text into the safe area'
          )
        }
      ));
    }
    if (text.fontSize < minimumFontSize) {
      issues.push(issue(
        page,
        'TEXT_TOO_SMALL',
        'error',
        text.id,
        `This text uses ${text.fontSize}px type, below the ${minimumFontSize}px readable minimum.`,
        `Increase the font size to at least ${minimumFontSize}px and reflow the text.`,
        {
          autoFix: reflowFix(
            'reflow-readable-text',
            `Reflow at ${minimumFontSize}px or larger`
          )
        }
      ));
    }

    if (options.measureTextOverflow && String(text.content || '').length > 0) {
      try {
        const measurement = await options.measureTextOverflow({ page, textElement: text, bounds });
        if (overflowResultIsTrue(measurement)) {
          issues.push(issue(
            page,
            'TEXT_OVERFLOW',
            'error',
            text.id,
            'The measured text does not fit inside its text box.',
            'Expand the text box, reduce the copy, or split the narration onto another page.',
            {
              autoFix: reflowFix(
                'reflow-readable-text',
                'Reflow and paginate this text'
              )
            }
          ));
        }
      } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);
        issues.push(issue(
          page,
          'TEXT_MEASUREMENT_FAILED',
          'error',
          text.id,
          `Text overflow could not be measured${reason ? `: ${reason}` : '.'}`,
          'Load the configured production font and measure the text again before export.',
          {
            autoFix: reflowFix(
              'use-production-font',
              'Reload the production font and reflow'
            )
          }
        ));
      }
    }
  }

  const texts = (page.textElements || []).filter((text) => validTextIds.has(text.id));
  for (const text of texts.filter((candidate) => String(candidate.content || '').trim().length > 0)) {
    const bounds = textBounds.get(text.id)!;
    const overlapsImage = (page.imageElements || []).some((image) => (
      validImageIds.has(image.id) && intersects(bounds, imageBounds.get(image.id)!)
    ));
    const backdrop = resolveStaticTextBackdrop(page, text, overlapsImage);
    let contrast: TextContrastMeasurement | null = null;
    let contrastFailureReason = '';

    if (backdrop) {
      const ratio = calculateContrastRatio(text.color, cssColor(backdrop));
      if (ratio !== null) contrast = { ratio, backgroundColor: cssColor(backdrop) };
      else contrastFailureReason = 'the text color is not a supported CSS color';
    } else if (options.measureTextContrast) {
      try {
        contrast = normalizedContrastMeasurement(
          await options.measureTextContrast({ page, textElement: text, bounds })
        );
        if (!contrast) contrastFailureReason = 'the rendered contrast measurement was invalid';
      } catch (error) {
        contrastFailureReason = error instanceof Error ? error.message : String(error);
      }
    } else {
      contrastFailureReason = overlapsImage && !hasOpaqueTextPanel(text)
        ? 'the text sits on artwork without an opaque reading panel'
        : 'the rendered background color could not be determined';
    }

    if (!contrast) {
      issues.push(issue(
        page,
        'TEXT_CONTRAST_UNVERIFIED',
        'warning',
        text.id,
        `Text contrast could not be verified${contrastFailureReason ? ` because ${contrastFailureReason}` : ''}.`,
        'Measure the rendered artwork or add an opaque, high-contrast reading panel.',
        {
          autoFix: patchFix(
            'add-readable-text-panel',
            'Add a high-contrast reading panel',
            'text',
            text.id,
            { color: '#111111', backgroundColor: '#ffffff', backgroundOpacity: 1 }
          )
        }
      ));
      continue;
    }

    const recommendedRatio = recommendedContrastForText(
      text,
      recommendedContrastRatio,
      criticalContrastRatio
    );
    if (contrast.ratio < recommendedRatio - BOUNDS_EPSILON) {
      const severity: PreflightSeverity = contrast.ratio < criticalContrastRatio
        ? 'error'
        : 'warning';
      const blackRatio = backdrop ? calculateContrastRatio('#000000', cssColor(backdrop)) : null;
      const whiteRatio = backdrop ? calculateContrastRatio('#ffffff', cssColor(backdrop)) : null;
      const recommendedTextColor = blackRatio !== null && whiteRatio !== null
        ? (blackRatio >= whiteRatio ? '#000000' : '#ffffff')
        : undefined;
      issues.push(issue(
        page,
        'TEXT_LOW_CONTRAST',
        severity,
        text.id,
        `This text has a ${contrast.ratio.toFixed(2)}:1 contrast ratio${severity === 'error' ? `, below the ${criticalContrastRatio}:1 blocking minimum` : `, below the ${recommendedRatio}:1 readability target`}.`,
        'Choose a higher-contrast text color or add an opaque reading panel behind the text.',
        {
          autoFix: recommendedTextColor
            ? patchFix(
                'increase-text-contrast',
                `Use ${recommendedTextColor === '#000000' ? 'black' : 'white'} text`,
                'text',
                text.id,
                { color: recommendedTextColor }
              )
            : patchFix(
                'add-readable-text-panel',
                'Add a high-contrast reading panel',
                'text',
                text.id,
                { color: '#111111', backgroundColor: '#ffffff', backgroundOpacity: 1 }
              ),
          metrics: {
            contrastRatio: contrast.ratio,
            recommendedContrastRatio: recommendedRatio,
            criticalContrastRatio,
            recommendedTextColor
          }
        }
      ));
    }
  }

  for (let firstIndex = 0; firstIndex < texts.length; firstIndex += 1) {
    for (let secondIndex = firstIndex + 1; secondIndex < texts.length; secondIndex += 1) {
      const first = texts[firstIndex];
      const second = texts[secondIndex];
      if (intersects(textBounds.get(first.id)!, textBounds.get(second.id)!)) {
        issues.push(issue(
          page,
          'TEXT_COLLISION',
          'error',
          first.id,
          'Two text elements overlap and may obscure each other.',
          'Move or reflow the text boxes so their visual bounds do not intersect.',
          { relatedElementId: second.id }
        ));
      }
    }
  }

  const allRegions = [...(page.subjectRegions || []), ...(page.saliencyRegions || [])];
  const validRegions: NormalizedRegion[] = [];
  for (const region of allRegions) {
    if (normalizedRegionIsValid(region)) {
      if (region.role !== 'text-safe') validRegions.push(region);
    } else {
      issues.push(issue(
        page,
        'SEMANTIC_REGION_INVALID',
        'error',
        null,
        `The semantic region "${region.label || region.id || 'unnamed'}" has invalid normalized geometry.`,
        'Regenerate subject analysis so every region is finite, positive, and contained within 0–1 coordinates.',
        { regionId: region.id }
      ));
    }
  }

  const pageAllowsOverlap = page.layout?.allowSubjectOverlap === true;
  for (const text of texts) {
    if (pageAllowsOverlap || text.allowSubjectOverlap) continue;
    const bounds = textBounds.get(text.id)!;
    for (const region of validRegions) {
      if (intersects(bounds, regionToCanvasBounds(region, canvasSize))) {
        issues.push(issue(
          page,
          'TEXT_SUBJECT_OVERLAP',
          'error',
          text.id,
          `This text overlaps the protected ${region.label || region.role} region.`,
          'Move the text away from the subject, choose a protected template, or explicitly allow the overlap.',
          { regionId: region.id }
        ));
      }
    }
  }

  if (texts.length > 0 && validRegions.length === 0) {
    const overlappingText = texts.find((text) => {
      if (pageAllowsOverlap || text.allowSubjectOverlap) return false;
      const bounds = textBounds.get(text.id)!;
      return (page.imageElements || []).some((image) => (
        validImageIds.has(image.id) && intersects(bounds, imageBounds.get(image.id)!)
      ));
    });
    if (overlappingText) {
      issues.push(issue(
        page,
        'SEMANTIC_REGIONS_MISSING',
        'error',
        overlappingText.id,
        'This image-overlay layout has no valid subject or saliency regions to protect.',
        'Analyze the final image for important regions, use a protected template, or explicitly approve subject overlap.'
      ));
    }
  }

  const fullBleedImages = (page.imageElements || []).filter((image) => image.bleed === 'full');
  const requiresFullBleed = page.layout?.template === 'full-bleed-caption';
  if (requiresFullBleed && fullBleedImages.length === 0) {
    issues.push(issue(
      page,
      'FULL_BLEED_COVERAGE_MISSING',
      'error',
      null,
      'The full-bleed caption template has no image marked for full bleed.',
      'Mark the background illustration as full bleed and fit it with cover.'
    ));
  }
  for (const image of fullBleedImages) {
    if (!validImageIds.has(image.id) || image.fit !== 'cover' || !coversTrim(image, canvasSize)) {
      const canFixWithCover = validImageIds.has(image.id)
        && image.fit !== 'cover'
        && coversTrim(image, canvasSize);
      issues.push(issue(
        page,
        'FULL_BLEED_COVERAGE_MISSING',
        'error',
        image.id,
        'This full-bleed image does not cover every trim corner with cover fitting.',
        'Use cover fitting and resize the image so it completely covers the page before bleed extension.',
        canFixWithCover ? {
          autoFix: patchFix(
            'use-cover-fit',
            'Use cover fitting',
            'image',
            image.id,
            { fit: 'cover' }
          )
        } : undefined
      ));
    }
  }

  if ((options.printSettings?.bleedInches || 0) > 0) {
    for (const image of (page.imageElements || []).filter(candidate => validImageIds.has(candidate.id))) {
      const bounds = imageBounds.get(image.id)!;
      const touchesTrimEdge = bounds.left <= BOUNDS_EPSILON
        || bounds.top <= BOUNDS_EPSILON
        || bounds.right >= canvasSize.width - BOUNDS_EPSILON
        || bounds.bottom >= canvasSize.height - BOUNDS_EPSILON;
      if (touchesTrimEdge && image.bleed !== 'full' && image.fit !== 'cover') {
        issues.push(issue(
          page,
          'EDGE_BLEED_FIT_MISSING',
          'error',
          image.id,
          'An image touching the trim edge is not configured to cover its print bleed.',
          'Use cover fitting, move the image inside the trim, or choose a non-bleed export.',
          {
            autoFix: patchFix(
              'use-cover-fit',
              'Use cover fitting for bleed',
              'image',
              image.id,
              { fit: 'cover' }
            )
          }
        ));
      }
    }
  }

  return issues;
};

export const preflightBook = async (
  pages: Page[],
  options: PagePreflightOptions
): Promise<PreflightIssue[]> => {
  const pageIssues = await Promise.all((pages || []).map((page) => preflightPage(page, options)));
  return pageIssues.flat();
};

export const summarizePreflight = (issues: PreflightIssue[]): PreflightSummary => {
  const byCode: Partial<Record<PreflightIssueCode, number>> = {};
  let errorCount = 0;
  let warningCount = 0;

  for (const current of issues) {
    byCode[current.code] = (byCode[current.code] || 0) + 1;
    if (current.severity === 'error') errorCount += 1;
    else warningCount += 1;
  }

  return {
    total: issues.length,
    errorCount,
    warningCount,
    hasErrors: errorCount > 0,
    hasWarnings: warningCount > 0,
    isClean: issues.length === 0,
    canExport: errorCount === 0,
    byCode
  };
};
